import type {
  AnswerPersistenceAdapter,
  PersistedSnapshot,
  PersistenceListener,
  Unsubscribe,
} from '@inside-us-aos/form-engine';

/**
 * Phase 2 batch 3 Slice 5 — localStorage-backed persistence adapter.
 *
 * Implements the `AnswerPersistenceAdapter` contract against the
 * browser's `localStorage`. Keys are namespaced under
 * `inside-us-aos:applications:{applicationId}` so multiple
 * applications never collide (Phase 5 will key by Firebase UID).
 *
 * `localStorage.setItem` is synchronous, which means typed answers
 * survive a browser refresh — exactly what the demo needs to feel
 * like a real app. Phase 5 swaps the adapter for a Firestore
 * implementation that reuses the same interface; the engine's
 * hydrate/persist code stays untouched.
 *
 * MDN refs:
 *   - https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
 *   - https://developer.mozilla.org/en-US/docs/Web/API/Window/storage_event
 */

const KEY_PREFIX = 'inside-us-aos:applications:';

const keyFor = (applicationId: string): string =>
  `${KEY_PREFIX}${applicationId}`;

export interface CreateLocalStoragePersistenceAdapterOptions {
  /**
   * Override the storage backend — useful for tests that want to
   * pass a synthetic store instead of `globalThis.localStorage`.
   */
  storage?: Storage;
}

/**
 * Creates a persistence adapter that round-trips through the
 * browser's localStorage. The returned adapter satisfies the same
 * `AnswerPersistenceAdapter` contract as the in-memory adapter; the
 * engine treats them interchangeably.
 *
 * Cross-tab sync via the native `storage` event is wired up so the
 * `subscribe` callback fires when another tab writes a snapshot for
 * the same application. (No-op in test environments where window is
 * absent.)
 */
export function createLocalStoragePersistenceAdapter(
  options: CreateLocalStoragePersistenceAdapterOptions = {},
): AnswerPersistenceAdapter {
  const storage =
    options.storage ??
    (typeof globalThis !== 'undefined' && 'localStorage' in globalThis
      ? (globalThis as { localStorage: Storage }).localStorage
      : undefined);

  if (!storage) {
    throw new Error(
      'createLocalStoragePersistenceAdapter: no localStorage available',
    );
  }

  const listeners = new Map<string, Set<PersistenceListener>>();

  // Cross-tab sync — the `storage` event only fires in OTHER tabs, so
  // this catches refreshes / background tabs writing the same key.
  const onStorageEvent = (event: StorageEvent): void => {
    if (!event.key || !event.key.startsWith(KEY_PREFIX)) return;
    const applicationId = event.key.slice(KEY_PREFIX.length);
    const set = listeners.get(applicationId);
    if (!set || !event.newValue) return;
    try {
      const snapshot = JSON.parse(event.newValue) as PersistedSnapshot;
      for (const listener of [...set]) {
        try {
          listener(snapshot);
        } catch (err) {
          // Isolate listener errors — see audit emitter for the
          // same fan-out pattern.
          queueMicrotask(() => {
            throw err;
          });
        }
      }
    } catch {
      // Corrupt JSON — drop silently. Phase 5 will surface this as
      // a `partial_replay_failure` warning.
    }
  };

  if (
    typeof globalThis !== 'undefined' &&
    'addEventListener' in globalThis &&
    options.storage === undefined
  ) {
    (globalThis as unknown as Window).addEventListener(
      'storage',
      onStorageEvent,
    );
  }

  return {
    load: async (applicationId, opts) => {
      await Promise.resolve();
      const raw = storage.getItem(keyFor(applicationId));
      if (raw === null) {
        return { snapshot: null, warnings: [] };
      }
      let snapshot: PersistedSnapshot;
      try {
        snapshot = JSON.parse(raw) as PersistedSnapshot;
      } catch {
        return { snapshot: null, warnings: [] };
      }
      const warnings = [];
      if (
        opts?.expectedSchemaVersion &&
        opts.expectedSchemaVersion !== snapshot.schemaVersion
      ) {
        warnings.push({
          code: 'schema_version_mismatch' as const,
          message:
            `Persisted snapshot for application ${applicationId} was written ` +
            `at schemaVersion ${snapshot.schemaVersion}, but the engine expects ` +
            `${opts.expectedSchemaVersion}.`,
          found: snapshot.schemaVersion,
          expected: opts.expectedSchemaVersion,
        });
      }
      return { snapshot, warnings };
    },

    save: async (applicationId, snapshot) => {
      await Promise.resolve();
      const serialized = JSON.stringify(snapshot);
      storage.setItem(keyFor(applicationId), serialized);
      // Notify same-tab listeners (the native `storage` event only
      // fires in other tabs, so we fan out manually here).
      const set = listeners.get(applicationId);
      if (set && set.size > 0) {
        for (const listener of [...set]) {
          try {
            listener(snapshot);
          } catch (err) {
            queueMicrotask(() => {
              throw err;
            });
          }
        }
      }
    },

    subscribe: (
      applicationId: string,
      listener: PersistenceListener,
    ): Unsubscribe => {
      let set = listeners.get(applicationId);
      if (!set) {
        set = new Set();
        listeners.set(applicationId, set);
      }
      set.add(listener);
      return () => {
        set?.delete(listener);
        if (set && set.size === 0) listeners.delete(applicationId);
      };
    },
  };
}
